import { Component, inject, OnDestroy, signal, computed, effect, ChangeDetectionStrategy } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ProgressBar } from 'primeng/progressbar';
import { ProgressSpinner } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { Subject, interval, takeUntil, switchMap, firstValueFrom, filter, tap } from 'rxjs';
import {
  ApprovedSystemsService,
  BulkDownloadStatusResponse,
  StartBulkDownloadResponse,
  CertificateTypeFilter,
} from '../../services/approved-systems.service';

@Component({
  selector: 'app-bulk-download-dialog',
  standalone: true,
  imports: [ButtonModule, DividerModule, ProgressBar, ProgressSpinner, RadioButtonModule, FormsModule],
  templateUrl: './bulk-download-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./bulk-download-dialog.component.scss'],
})
export class BulkDownloadDialogComponent implements OnDestroy {
  private dialogRef = inject(DynamicDialogRef);
  private messageService = inject(MessageService);
  private approvedSystemsService = inject(ApprovedSystemsService);
  private destroy$ = new Subject<void>();

  // Year options
  currentYear = new Date().getFullYear();
  nextYear = this.currentYear + 1;

  // Form state
  certificationYear = signal(this.nextYear);
  typeFilter = signal<CertificateTypeFilter>('All');

  // Certificate count preview
  certificateCount = signal(0);
  loadingCount = signal(true);

  // Batch state
  batchId = signal<string | null>(null);
  status = signal<BulkDownloadStatusResponse | null>(null);

  // Loading states
  starting = signal(false);
  polling = signal(false);
  downloading = signal(false);

  // Computed values
  progressPercent = computed(() => {
    const s = this.status();
    if (!s || s.totalCount === 0) return 0;
    return s.progressPercent;
  });

  isComplete = computed(() => this.status()?.isComplete ?? false);
  isFailed = computed(() => this.status()?.isFailed ?? false);
  canDownload = computed(() => this.isComplete() && !this.isFailed() && !!this.status()?.zipBlobRef);
  canClose = computed(() => !this.starting() && (this.isComplete() || this.isFailed() || !this.batchId()));

  // Type filter options
  typeFilterOptions = [
    { label: 'All Certificates', value: 'All' as CertificateTypeFilter },
    { label: 'Coating Systems Only', value: 'CoatingSystems' as CertificateTypeFilter },
    { label: 'Pretreatment Systems Only', value: 'PretreatmentSystems' as CertificateTypeFilter },
  ];

  constructor() {
    // Re-fetch count when year or filter changes
    effect(() => {
      const year = this.certificationYear();
      const filter = this.typeFilter();
      // Only fetch if we haven't started a batch yet
      if (!this.batchId()) {
        this.loadCertificateCount(year, filter);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCertificateCount(year: number, typeFilter: CertificateTypeFilter): void {
    this.loadingCount.set(true);
    this.approvedSystemsService.getBulkDownloadCertificateCount(year, typeFilter).subscribe({
      next: (response) => {
        this.certificateCount.set(response.count);
        this.loadingCount.set(false);
      },
      error: (err) => {
        console.error('Failed to load certificate count:', err);
        this.loadingCount.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load certificate count',
          life: 5000,
        });
      },
    });
  }

  async startDownload(): Promise<void> {
    if (this.starting() || this.batchId()) return;

    this.starting.set(true);

    try {
      const response: StartBulkDownloadResponse = await firstValueFrom(
        this.approvedSystemsService.startBulkDownload(this.certificationYear(), this.typeFilter())
      );

      if (!response.batchId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No Certificates',
          detail: 'No certificates found matching the specified criteria',
          life: 5000,
        });
        this.starting.set(false);
        return;
      }

      this.batchId.set(response.batchId);

      // Initialize status with initial values
      this.status.set({
        batchId: response.batchId,
        totalCount: response.totalCertificates,
        processedCount: 0,
        progressPercent: 0,
        isComplete: false,
        isFailed: false,
      });

      this.messageService.add({
        severity: 'info',
        summary: 'Download Started',
        detail: `Preparing ${response.totalCertificates} certificates for download...`,
        life: 3000,
      });

      // Start polling for status
      this.startPolling();
    } catch (error: unknown) {
      console.error('Failed to start bulk download:', error);
      const httpError = error as { error?: { message?: string } };
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: httpError.error?.message || 'Failed to start bulk download',
        life: 5000,
      });
    } finally {
      this.starting.set(false);
    }
  }

  private startPolling(): void {
    const batchId = this.batchId();
    if (!batchId) return;

    this.polling.set(true);

    interval(3000)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.isComplete() && !this.isFailed()),
        switchMap(() => this.approvedSystemsService.getBulkDownloadStatus(batchId)),
        tap((status) => {
          this.status.set(status);

          if (status.isComplete || status.isFailed) {
            this.polling.set(false);
            this.onComplete(status);
          }
        })
      )
      .subscribe({
        error: (err) => {
          console.error('Failed to get download status:', err);
          this.polling.set(false);
        },
      });
  }

  private onComplete(status: BulkDownloadStatusResponse): void {
    if (status.isFailed) {
      this.messageService.add({
        severity: 'error',
        summary: 'Download Failed',
        detail: status.error || 'Failed to generate zip file',
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Download Ready',
        detail: `${status.processedCount} certificates packaged successfully. Click "Download Zip" to get your file.`,
        life: 5000,
      });
    }
  }

  downloadZip(): void {
    const batchId = this.batchId();
    if (!batchId || this.downloading()) return;

    this.downloading.set(true);

    // Generate filename based on selected options
    const year = this.certificationYear();
    const typeLabel =
      this.typeFilter() === 'All' ? 'all' : this.typeFilter() === 'CoatingSystems' ? 'coating' : 'pretreatment';
    const filename = `certificates-${year}-${typeLabel}.zip`;

    this.approvedSystemsService.downloadBulkCertificates(batchId, filename);

    // Close dialog after download starts (ZIP is deleted after download, can't re-download)
    setTimeout(() => {
      this.downloading.set(false);
      this.dialogRef.close({ completed: true, downloaded: true });
    }, 1000);
  }

  close(): void {
    this.dialogRef.close({
      completed: this.isComplete(),
      status: this.status(),
    });
  }
}
